import type { Href } from 'expo-router';

import publicWebManifestJson from '../../i18n/public-web.generated.json' with { type: 'json' };
import localeRegistryJson from '../../i18n/locales.generated.json' with { type: 'json' };
import type { MessageLocale } from './locales';

type SurfaceOwner = 'backend-crawler' | 'frontend-static' | 'shared';
type LocaleEligibility = 'excluded' | 'localized' | 'source-only';

export type PublicWebSurface = {
    id: string;
    pathname: string;
    owner: SurfaceOwner;
    contentClass: string;
    localeEligibility: LocaleEligibility;
    indexable: boolean;
    sitemap: boolean;
    dynamic: boolean;
    routePattern?: string;
    reason?: string;
    deferredRun?: number;
};

type PublicWebManifest = {
    schemaVersion: 1;
    canonicalOrigin: string;
    sourceLocale: string;
    urlPolicy: {
        prefixDefaultLocale: false;
        previewRobots: string;
        xDefaultLocale: string;
    };
    surfaces: PublicWebSurface[];
};

type LocaleDefinition = {
    tag: string;
    pathPrefix: string;
    stage: 'planned' | 'preview' | 'live' | 'retired';
    indexable: boolean;
};

type LocaleRegistry = {
    defaultLocale: string;
    locales: LocaleDefinition[];
    testLocales: Array<{ tag: string }>;
};

const manifest = publicWebManifestJson as PublicWebManifest;
const localeRegistry = localeRegistryJson as LocaleRegistry;
const surfacesById = new Map(manifest.surfaces.map((surface) => [surface.id, surface]));
const dynamicSurfaceMatchers = manifest.surfaces
    .filter((surface) => surface.dynamic && surface.routePattern)
    .map((surface) => ({ surface, pattern: new RegExp(surface.routePattern as string) }));

export const PUBLIC_WEB_CANONICAL_ORIGIN = manifest.canonicalOrigin;
export const PUBLIC_WEB_MANIFEST = manifest;

export function normalizePublicPathname(value: string): string {
    const pathOnly = value.split(/[?#]/, 1)[0] || '/';
    const withLeadingSlash = pathOnly.startsWith('/') ? pathOnly : `/${pathOnly}`;
    if (withLeadingSlash === '/') {
        return '/';
    }
    return withLeadingSlash.replace(/\/+$/, '');
}

export function getPublicWebSurface(surfaceId: string): PublicWebSurface {
    const surface = surfacesById.get(surfaceId);
    if (!surface) {
        throw new Error(`Unknown public-web surface: ${surfaceId}`);
    }
    return surface;
}

export function findPublicWebSurface(pathname: string): PublicWebSurface | null {
    const normalizedPathname = normalizePublicPathname(pathname);
    const exactSurface = manifest.surfaces.find((surface) => (
        !surface.dynamic && surface.pathname === normalizedPathname
    ));
    if (exactSurface) {
        return exactSurface;
    }

    return dynamicSurfaceMatchers.find(({ pattern }) => pattern.test(normalizedPathname))
        ?.surface ?? null;
}

function localeDefinition(locale: string): LocaleDefinition | null {
    return localeRegistry.locales.find(({ tag }) => tag === locale) ?? null;
}

function activeBuildTarget(): 'production' | 'beta' | 'review' {
    const value = process.env.EXPO_PUBLIC_PZ_I18N_BUILD_TARGET || 'production';
    if (value === 'production' || value === 'beta' || value === 'review') {
        return value;
    }
    throw new Error(`Unsupported i18n build target: ${value}`);
}

export function isProductionPublicBuild(): boolean {
    return activeBuildTarget() === 'production';
}

function isLocaleVisibleForActiveBuild(definition: LocaleDefinition): boolean {
    const target = activeBuildTarget();
    if (definition.stage === 'retired') return false;
    if (target === 'production') return definition.stage === 'live';
    if (target === 'beta') {
        return definition.stage === 'live' || definition.stage === 'preview';
    }
    return process.env.EXPO_PUBLIC_PZ_I18N_REVIEW_ARTIFACT === 'non-promotable'
        && ['planned', 'preview', 'live'].includes(definition.stage);
}

function assertPublicUrlLocale(locale: string, allowTestLocale: boolean): LocaleDefinition | null {
    const managed = localeDefinition(locale);
    if (managed) {
        return isLocaleVisibleForActiveBuild(managed) ? managed : localeDefinition('en');
    }

    const isTestLocale = localeRegistry.testLocales.some(({ tag }) => tag === locale);
    if (!allowTestLocale || !isTestLocale || locale !== 'en-XA') {
        throw new Error(`Unsupported public URL locale: ${locale}`);
    }
    return null;
}

export function getPublicLocalePathPrefix(
    locale: string,
    { allowTestLocale = false }: { allowTestLocale?: boolean } = {}
): string {
    const managed = assertPublicUrlLocale(locale, allowTestLocale);
    if (managed) {
        return managed.pathPrefix ? `/${managed.pathPrefix}` : '';
    }
    return '/en-xa';
}

export function localizedPublicPath(
    pathname: string,
    locale: string,
    { allowTestLocale = false }: { allowTestLocale?: boolean } = {}
): string {
    const normalizedPathname = normalizePublicPathname(pathname);
    const surface = findPublicWebSurface(normalizedPathname);
    if (!surface || surface.localeEligibility !== 'localized') {
        throw new Error(`Path is not an eligible localized public surface: ${normalizedPathname}`);
    }
    const prefix = getPublicLocalePathPrefix(locale, { allowTestLocale });
    return normalizedPathname === '/' ? `${prefix}/` : `${prefix}${normalizedPathname}`;
}

export function localizedPublicUrl(
    pathname: string,
    locale: string,
    options: { allowTestLocale?: boolean } = {}
): string {
    const localizedPathname = localizedPublicPath(pathname, locale, options);
    return localizedPathname === '/'
        ? PUBLIC_WEB_CANONICAL_ORIGIN
        : `${PUBLIC_WEB_CANONICAL_ORIGIN}${localizedPathname}`;
}

export function getSurfaceCanonicalPath(
    surfaceId: string,
    canonicalPathname?: string
): string {
    const surface = getPublicWebSurface(surfaceId);
    if (!surface.dynamic) {
        if (canonicalPathname && normalizePublicPathname(canonicalPathname) !== surface.pathname) {
            throw new Error(`Canonical path does not match public-web surface ${surfaceId}`);
        }
        return surface.pathname;
    }
    if (!canonicalPathname) {
        throw new Error(`Dynamic public-web surface ${surfaceId} requires a canonical pathname`);
    }
    const normalizedPathname = normalizePublicPathname(canonicalPathname);
    if (!surface.routePattern || !new RegExp(surface.routePattern).test(normalizedPathname)) {
        throw new Error(`Canonical path does not match public-web surface ${surfaceId}`);
    }
    return normalizedPathname;
}

export function publicCanonicalUrl(
    surfaceId: string,
    locale: string,
    canonicalPathname?: string,
    options: { allowTestLocale?: boolean } = {}
): string {
    return localizedPublicUrl(
        getSurfaceCanonicalPath(surfaceId, canonicalPathname),
        locale,
        options
    );
}

export type PublicAlternate = {
    hrefLang: string;
    href: string;
};

export function publicSeoAlternates(
    surfaceId: string,
    canonicalPathname?: string,
    currentLocale: string = manifest.sourceLocale
): PublicAlternate[] {
    const surface = getPublicWebSurface(surfaceId);
    const currentDefinition = localeDefinition(currentLocale);
    if (
        activeBuildTarget() !== 'production'
        || currentDefinition?.stage !== 'live'
        || !currentDefinition.indexable
        || !surface.indexable
        || surface.localeEligibility !== 'localized'
    ) {
        return [];
    }
    const liveIndexableLocales = localeRegistry.locales.filter((locale) => (
        locale.stage === 'live' && locale.indexable
    ));
    if (liveIndexableLocales.length < 2) {
        return [];
    }

    const pathname = getSurfaceCanonicalPath(surfaceId, canonicalPathname);
    const alternates = liveIndexableLocales.map((locale) => ({
        hrefLang: locale.tag,
        href: localizedPublicUrl(pathname, locale.tag),
    }));
    const defaultLocale = localeRegistry.defaultLocale;
    const defaultHref = alternates.find(({ hrefLang }) => hrefLang === defaultLocale)?.href;
    if (defaultHref) {
        alternates.push({ hrefLang: 'x-default', href: defaultHref });
    }
    return alternates;
}

export function publicRobotsPolicy(
    surfaceId: string,
    locale: string,
    explicitRobots?: string
): string | undefined {
    const surface = getPublicWebSurface(surfaceId);
    const definition = localeDefinition(locale);
    if (
        activeBuildTarget() !== 'production'
        || !surface.indexable
        || !definition
        || definition.stage !== 'live'
        || !definition.indexable
    ) {
        return manifest.urlPolicy.previewRobots;
    }
    return explicitRobots;
}

export function openGraphLocale(locale: string): string {
    const canonical = Intl.getCanonicalLocales(locale)[0] ?? locale;
    const maximized = new Intl.Locale(canonical).maximize();
    return maximized.region
        ? `${maximized.language}_${maximized.region}`
        : maximized.language;
}

export function isStaticPublicWebExport(): boolean {
    return process.env.EXPO_PUBLIC_PZ_STATIC_PUBLIC_EXPORT === '1';
}

export function getStaticPublicExportLocale(): MessageLocale | null {
    if (!isStaticPublicWebExport()) {
        return null;
    }
    const locale = process.env.EXPO_PUBLIC_PZ_I18N_LOCALE;
    if (!locale) {
        throw new Error('Static public export is missing EXPO_PUBLIC_PZ_I18N_LOCALE');
    }
    assertPublicUrlLocale(locale, process.env.EXPO_PUBLIC_PZ_STATIC_TEST_LOCALE === '1');
    return locale as MessageLocale;
}

export function getStaticPublicExportPrefix(): string {
    const locale = getStaticPublicExportLocale();
    if (!locale) {
        return '';
    }
    const allowTestLocale = process.env.EXPO_PUBLIC_PZ_STATIC_TEST_LOCALE === '1';
    const expectedPrefix = getPublicLocalePathPrefix(locale, { allowTestLocale });
    const configuredPrefix = process.env.EXPO_PUBLIC_PZ_PUBLIC_PATH_PREFIX ?? '';
    if (configuredPrefix !== expectedPrefix) {
        throw new Error(
            `Static public export prefix ${configuredPrefix || '<empty>'} does not match locale ${locale}`
        );
    }
    return configuredPrefix;
}

export function isLocalizedPublicPath(pathname: string): boolean {
    return findPublicWebSurface(pathname)?.localeEligibility === 'localized';
}

export function isPublicUrlLocaleAuthoritative(pathname: string): boolean {
    const surface = findPublicWebSurface(pathname);
    return Boolean(
        isStaticPublicWebExport()
        && surface
        && surface.localeEligibility !== 'excluded'
    );
}

export function stripStaticPublicExportPrefix(pathname: string): string {
    const normalizedPathname = normalizePublicPathname(pathname);
    const prefix = getStaticPublicExportPrefix();
    if (!prefix) {
        return normalizedPathname;
    }
    if (normalizedPathname === prefix) {
        return '/';
    }
    if (normalizedPathname.startsWith(`${prefix}/`)) {
        return normalizedPathname.slice(prefix.length) || '/';
    }
    return normalizedPathname;
}

export function stripManagedPublicLocalePrefix(pathname: string): string {
    const normalizedPathname = normalizePublicPathname(pathname);
    const prefixes = localeRegistry.locales
        .map(({ pathPrefix }) => pathPrefix)
        .filter(Boolean)
        .sort((left, right) => right.length - left.length);
    for (const pathPrefix of prefixes) {
        const prefix = `/${pathPrefix}`;
        if (normalizedPathname === prefix) return '/';
        if (normalizedPathname.startsWith(`${prefix}/`)) {
            return normalizedPathname.slice(prefix.length) || '/';
        }
    }
    return normalizedPathname;
}

/**
 * Build a same-origin, full-page public language navigation while preserving
 * the exact query string and fragment. Ineligible or incomplete surfaces have
 * no switch target.
 */
export function publicLocaleSwitchUrl(
    currentUrl: string,
    targetLocale: string
): string | null {
    const parsed = new URL(currentUrl, PUBLIC_WEB_CANONICAL_ORIGIN);
    const logicalPathname = stripManagedPublicLocalePrefix(parsed.pathname);
    const surface = findPublicWebSurface(logicalPathname);
    if (!surface || surface.localeEligibility !== 'localized') {
        return null;
    }
    parsed.pathname = localizedPublicPath(logicalPathname, targetLocale);
    return parsed.toString();
}

export function boundarySafeHref(href: Href): Href {
    if (typeof href !== 'string' || !href.startsWith('/') || !getStaticPublicExportPrefix()) {
        return href;
    }
    const pathname = normalizePublicPathname(href);
    if (isLocalizedPublicPath(pathname)) {
        return href;
    }
    // A prefixed Expo bundle must cross into the unprefixed app with a full
    // navigation. Use the deployment target, not the SEO canonical origin;
    // this also keeps server and first-client hrefs identical during hydration.
    const appOrigin = activeBuildTarget() === 'production'
        ? PUBLIC_WEB_CANONICAL_ORIGIN
        : 'https://beta.paperzilla.ai';
    return `${appOrigin}${href}` as Href;
}

export function staticHtmlPathname(relativeHtmlPath: string): string | null {
    const normalized = relativeHtmlPath.replaceAll('\\', '/').replace(/^\/+/, '');
    if (!normalized.endsWith('.html') || normalized.includes('/(') || normalized.startsWith('(')) {
        return null;
    }
    if (normalized.includes('[') || normalized.startsWith('+') || normalized.startsWith('_')) {
        return null;
    }
    if (normalized === 'index.html') {
        return '/';
    }
    const withoutExtension = normalized.slice(0, -'.html'.length);
    return normalizePublicPathname(withoutExtension.endsWith('/index')
        ? withoutExtension.slice(0, -'/index'.length)
        : withoutExtension);
}

export function isLocalizedStaticHtml(relativeHtmlPath: string): boolean {
    const pathname = staticHtmlPathname(relativeHtmlPath);
    if (!pathname) {
        return false;
    }
    const surface = findPublicWebSurface(pathname);
    return Boolean(
        surface
        && surface.localeEligibility === 'localized'
        && surface.owner !== 'backend-crawler'
    );
}
